import React from 'react';
import { Text, View, Page, Document, StyleSheet, Font } from '@react-pdf/renderer';
import {
  InvoiceHeader,
  SellerBuyerInfo,
  ItemsTable,
  SummaryTable,
  PageBackground,
  Signatures
} from './components';

// Register Inter font for better Vietnamese text support
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.ttf',
      fontWeight: 100
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyfMZhrib2Bg-4.ttf',
      fontWeight: 200
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuOKfMZhrib2Bg-4.ttf',
      fontWeight: 300
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf',
      fontWeight: 400
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf',
      fontWeight: 500
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf',
      fontWeight: 600
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
      fontWeight: 700
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyYMZhrib2Bg-4.ttf',
      fontWeight: 800
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuBWYMZhrib2Bg-4.ttf',
      fontWeight: 900
    }
  ]
});

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: 'Inter',
    padding: 10,
    paddingTop: 15,
    position: 'relative',
    backgroundColor: '#f8f9fa',
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  },

  // Background image style for all pages
  backgroundImage: {
    width: '98%',
    height: '99%',
    objectFit: 'fill',
    position: 'absolute',
    top: '3.5%',
    left: '2.5%',
    minHeight: '90%',
    maxHeight: '99%',
    transform: 'scale(1.1)',
    transformOrigin: 'center center',
    zIndex: 3,
    opacity: 0.7
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 1, // Ensure content is above background
    flex: 1,
    minHeight: '100%'
  },

  // Header styles - more compact
  header: {
    textAlign: 'center',
    marginBottom: 10,
    borderBottom: '1px solid black',
    paddingBottom: 8
  },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2
  },

  invoiceTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#eb0103',
    marginBottom: 2
  },

  invoiceSubtitle: {
    fontSize: 10,
    marginBottom: 4
  },

  textBold: {
    fontWeight: 700
  },

  invoiceInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    fontSize: 9
  },

  invoiceInfoLeft: {
    flex: 1
  },

  invoiceInfoRight: {
    flex: 1,
    textAlign: 'right'
  },

  // Seller and Buyer sections - more compact
  section: {
    marginBottom: 8,
    border: '1px solid black',
    borderRadius: 3,
    padding: 8,
    backgroundColor: '#fff'
  },

  label: {
    width: '35%',
    fontSize: 9,
    fontWeight: 'bold',
    color: '#555'
  },

  // Table styles - more compact with height limit
  table: {
    marginTop: 2,
    border: '1px solid black',
    maxHeight: 400
  },

  tableHeader: {
    flexDirection: 'row',
    borderBottom: '1px solid black',
    fontSize: 9,
    minHeight: 25
  },

  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px dashed #ccc',
    minHeight: 20,
    alignItems: 'center'
  },

  tableCell: {
    padding: 4,
    fontSize: 9,
    textAlign: 'center',
    borderRight: '1px solid black'
  },

  tableCellLeft: {
    padding: 4,
    fontSize: 9,
    textAlign: 'left',
    borderRight: '1px solid black',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },

  tableHeaderCell: {
    padding: 4,
    fontSize: 9,
    textAlign: 'center',
    borderRight: '1px solid black',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },

  tableHeaderCellLast: {
    padding: 4,
    fontSize: 9,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },

  // Summary section - more compact
  summary: {
    marginTop: 8,
    border: '1px solid black',
    borderRadius: 3,
    padding: 8,
    backgroundColor: '#fff'
  },

  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
    fontSize: 9
  },

  summaryLabel: {
    fontWeight: 'bold',
    color: '#333'
  },

  summaryValue: {
    color: '#333'
  },

  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
    paddingTop: 5,
    borderTop: '1px solid black',
    fontSize: 11,
    fontWeight: 'bold',
    color: '#007bff'
  },

  // Signature section - more compact
  signature: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },

  signatureBox: {
    width: '45%',
    textAlign: 'center',
    padding: 8
  },

  signatureTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#333'
  },

  signatureSpace: {
    height: 30,
    borderBottom: '1px solid #ccc',
    marginBottom: 5
  },

  // Footer - positioned at bottom of page for all pages
  footer: {
    marginTop: 'auto',
    textAlign: 'center',
    fontSize: 9,
    color: '#666',
    borderTop: '1px solid #eee',
    paddingTop: 5,
    paddingBottom: 10,
    position: 'relative',
    zIndex: 2
  },

  // Seller/Buyer styles
  sellerBuyerContainer: {
    marginBottom: 8,
    paddingBottom: 8
  },

  sellerContainer: {
    marginBottom: 8,
    borderBottom: '1px solid #333',
    paddingBottom: 8
  },

  buyerContainer: {
    marginBottom: 8,
    paddingBottom: 8
  },

  sellerBuyerTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#333',
    textDecoration: 'underline'
  },

  sellerBuyerContent: {
    marginLeft: 10
  },

  sellerBuyerText: {
    fontSize: 10,
    marginBottom: 3,
    lineHeight: 1.2
  },

  sellerBuyerLabel: {
    fontSize: 9
  },

  sellerBuyerValue: {
    fontSize: 9
  },

  // Utility classes - adjusted for compact layout
  col1: { width: '6%' },
  col2: { width: '26%' },
  col3: { width: '8%' },
  col4: { width: '12%' },
  col5: { width: '12%' },
  col6: { width: '12%' },
  col7: { width: '10%' },
  col8: { width: '14%' }
});

interface EInvoiceTemplateProps {
  sellerInfo?: {
    seller: string;
    sellerTaxCode: string;
    sellerAddress: string;
  };
  invoiceData?: {
    date?: string;
    taxAuthorityCode?: string;
    serialNo?: string;
    invoiceNo?: string;
    orderNo?: string;
    buyerName?: string;
    paymentMethod?: string;
    identityCard?: string;
    passport?: string;
    company?: string;
    companyAddress?: string;
    taxCode?: string;
    items?: Array<{
      name: string;
      unit: string;
      quantity: number;
      unitPrice: number;
      amountBeforeTax: number;
      taxRate: number;
      taxAmount: number;
      amountAfterTax: number;
    }>;
    totalBeforeTax?: number;
    totalTax?: number;
    totalAmount?: number;
    totalAmountInWords?: string;
    totalsByTaxRate?: Record<
      number,
      {
        totalBeforeTax: number;
        totalTax: number;
        totalAmount: number;
      }
    >;
    signedBy?: string;
    signedDate?: string;
    verificationCode?: string;
  };
}

const EInvoiceTemplate: React.FC<EInvoiceTemplateProps> = ({ sellerInfo, invoiceData }) => {
  // Default values to prevent undefined errors
  const defaultSellerInfo = {
    seller: 'CÔNG TY TNHH KIN LIVING MANAGEMENT',
    sellerTaxCode: '0317936630',
    sellerAddress:
      'Số 13 Đường N2, Jamona Golden Silk, Phường Tân Thuận, Thành phố Hồ Chí Minh, Việt Nam'
  };

  const defaultInvoiceData = {
    date: '',
    serialNo: '',
    orderNo: '',
    buyerName: '',
    paymentMethod: '',
    identityCard: '',
    passport: '',
    company: '',
    companyAddress: '',
    taxCode: '',
    items: [],
    totalBeforeTax: 0,
    totalTax: 0,
    totalAmount: 0,
    totalAmountInWords: '',
    totalsByTaxRate: {}
  };

  const finalSellerInfo = sellerInfo || defaultSellerInfo;
  const finalInvoiceData = invoiceData || defaultInvoiceData;

  // Calculate items based on table height limit
  const maxTableHeight = 200; // Maximum height for table in pixels
  const rowHeight = 25; // Height per row in pixels
  const headerHeight = 30; // Height for table header
  const maxRowsPerPage = Math.floor((maxTableHeight - headerHeight) / rowHeight);

  const totalItems = finalInvoiceData.items?.length || 0;

  // Calculate pages: if no items, only 1 page. Otherwise calculate based on items
  const totalPages = totalItems === 0 ? 1 : Math.max(1, Math.ceil(totalItems / maxRowsPerPage));

  return (
    <Document>
      {/* Generate pages dynamically */}
      {Array.from({ length: totalPages }, (_, pageIndex) => {
        const startIndex = pageIndex * maxRowsPerPage;
        const isLastPage = pageIndex === totalPages - 1;

        // For last page, show all remaining items. For other pages, show limited items
        const endIndex = isLastPage
          ? finalInvoiceData.items?.length || 0
          : Math.min(startIndex + maxRowsPerPage, finalInvoiceData.items?.length || 0);
        const pageItems = finalInvoiceData.items?.slice(startIndex, endIndex) || [];

        return (
          <Page key={pageIndex} size="A4" style={styles.page}>
            {/* Background Image - First element in Page */}
            <PageBackground />

            {/* Content Wrapper */}
            <View style={styles.contentWrapper}>
              <InvoiceHeader invoiceData={finalInvoiceData} />
              <SellerBuyerInfo sellerInfo={finalSellerInfo} invoiceData={finalInvoiceData} />

              {/* Items table for this page */}
              {pageItems.length > 0 && <ItemsTable items={pageItems} startIndex={startIndex} />}

              {/* Summary table and signatures only on last page */}
              {isLastPage && (
                <>
                  <SummaryTable invoiceData={finalInvoiceData} />
                  <Signatures />
                </>
              )}

              {/* Footer - positioned at bottom of page for all pages */}
              <View style={styles.footer}>
                <Text>Tra cứu hóa đơn tại website: https://tracuuhoadon.minvoice.com.vn/</Text>
                <Text>Mã tra cứu: </Text>
                <Text>
                  Khởi tạo từ Phần mềm M-INVOICE - CÔNG TY TNHH HÓA ĐƠN ĐIỆN TỬ M-INVOICE - MST:
                  0106026495 - Hotline: 0901 80 16 18
                </Text>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

export default EInvoiceTemplate;
